import { useApi } from './api';
import type { 
  PhoneCall, 
  PhoneCallCreateRequest, 
  PhoneCallResponse, 
  AgentDashboardStats, 
  Specialist, 
  SpecialistsResponse, 
  SpecialistsStats,
  PhoneCallAgentFilters 
} from '../types/phoneCapture';
import { PHONE_CALL_AGENT_SCOPE_VALUES, PHONE_CALL_AGENT_STATUS_VALUES } from '../types/phoneCapture';
import { AVISO_PRIVACIDAD, avisoPrivacidadPlainText } from '../content/avisoPrivacidad';

export const usePhoneCallService = () => {
  const { apiFetch } = useApi();

  // Crear borrador de llamada telefónica (draft flow)
  const createPhoneCallDraft = async (data: any): Promise<any> => {
    const res = await apiFetch('/phone-calls/drafts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Error al crear el borrador de llamada telefónica');
    return res.json();
  };

  // Actualizar borrador de llamada telefónica (draft flow)
  const updatePhoneCallDraft = async (id: number, data: any): Promise<any> => {
    const res = await apiFetch(`/phone-calls/${id}/draft`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Error al actualizar el borrador de llamada telefónica');
    return res.json();
  };

  /**
   * Finalizar borrador (docs/phonecall-draft-contract.md): POST /phone-calls/{id}/finalize
   * Pasa la llamada a CAPTURED y dispara generación de claims según generateClaims.
   */
  const finalizePhoneCallDraft = async (
    id: number,
    body?: { generateClaims?: boolean; duration?: string }
  ): Promise<any> => {
    const payload: Record<string, unknown> = {
      generateClaims: body?.generateClaims !== false
    };
    if (body?.duration != null && String(body.duration).trim() !== '') {
      payload.duration = body.duration;
    }
    const res = await apiFetch(`/phone-calls/${id}/finalize`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Error al finalizar la llamada telefónica');
    return res.json();
  };

  /** Draft flow: asignar especialista a un referral del borrador */
  const assignSpecialistToDraftReferral = async (
    phoneCallId: number,
    referralId: number,
    assignedSpecialistId: number
  ): Promise<any> => {
    const payload = { referralId, assignedSpecialistId };
    const res = await apiFetch(`/phone-calls/${phoneCallId}/assign-specialist`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Error al asignar especialista al cliente del borrador');
    return res.json();
  };

  /**
   * Actualiza el semáforo de severidad (docs/phone-call-severity-level-api.md).
   * Solo permitido mientras la llamada está en estado DRAFT.
   */
  const updatePhoneCallSeverityLevel = async (
    id: number,
    severityLevel: 'GREEN' | 'YELLOW' | 'RED'
  ): Promise<PhoneCallResponse> => {
    const res = await apiFetch(`/phone-calls/${id}/severity-level`, {
      method: 'PUT',
      body: JSON.stringify({ severityLevel }),
    });
    if (!res.ok) {
      if (res.status === 404) throw new Error('Llamada no encontrada');
      if (res.status === 400) {
        throw new Error('No se puede actualizar el semáforo en este estado');
      }
      throw new Error('Error al guardar la severidad');
    }
    return res.json();
  };

  // Crear nueva llamada telefónica
  const createPhoneCall = async (data: PhoneCallCreateRequest): Promise<PhoneCallResponse> => {
    const res = await apiFetch('/phone-calls', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Error al crear la llamada telefónica');
    return res.json();
  };

  // Actualizar llamada telefónica existente
  const updatePhoneCall = async (id: number, data: PhoneCallCreateRequest): Promise<PhoneCallResponse> => {
    const res = await apiFetch(`/phone-calls/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Error al actualizar la llamada telefónica');
    return res.json();
  };

  /**
   * Valida filtros según FRONTEND-FILTROS-LLAMADAS-TELEFONICAS.md.
   * - status: debe ser uno de los valores del enum.
   * - dateFrom y dateTo: si ambos están presentes, dateFrom <= dateTo.
   */
  const validatePhoneCallAgentFilters = (filters?: PhoneCallAgentFilters): void => {
    if (!filters) return;
    if (filters.status && filters.status !== 'ALL') {
      const valid = (PHONE_CALL_AGENT_STATUS_VALUES as readonly string[]).includes(filters.status);
      if (!valid) {
        throw new Error(
          `Estado no válido. Valores permitidos: ${PHONE_CALL_AGENT_STATUS_VALUES.join(', ')}`
        );
      }
    }
    if (filters.scope) {
      const validScope = (PHONE_CALL_AGENT_SCOPE_VALUES as readonly string[]).includes(filters.scope);
      if (!validScope) {
        throw new Error(
          `Scope no válido. Valores permitidos: ${PHONE_CALL_AGENT_SCOPE_VALUES.join(', ')}`
        );
      }
    }
    if (filters.dateFrom && filters.dateTo) {
      if (filters.dateFrom > filters.dateTo) {
        throw new Error('La fecha "Desde" no puede ser posterior a la fecha "Hasta".');
      }
    }
  };

  // Obtener llamadas del agente actual (filtradas por createdBy)
  // ✅ Según FRONTEND-FILTROS-LLAMADAS-TELEFONICAS.md: paginación, ordenación y filtros opcionales
  const getAgentPhoneCalls = async (
    page = 0,
    size = 10,
    sortBy: string = 'callDate',
    sortDir: 'asc' | 'desc' = 'desc',
    filters?: PhoneCallAgentFilters
  ): Promise<any> => {
    validatePhoneCallAgentFilters(filters);

    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });

    if (filters?.employeeName?.trim()) {
      params.append('employeeName', filters.employeeName.trim());
    }
    if (filters?.patientName?.trim()) {
      params.append('patientName', filters.patientName.trim());
    }
    if (filters?.companyName?.trim()) {
      params.append('companyName', filters.companyName.trim());
    }
    if (filters?.status && filters.status !== 'ALL') {
      params.append('status', filters.status);
    }
    if (filters?.dateFrom) {
      params.append('dateFrom', filters.dateFrom);
    }
    if (filters?.dateTo) {
      params.append('dateTo', filters.dateTo);
    }
    if (filters?.scope) {
      params.append('scope', filters.scope);
    }

    const url = `/phone-calls/agent?${params.toString()}`;
    console.log('🔍 [getAgentPhoneCalls] Fetching with URL:', url);

    const res = await apiFetch(url);

    if (!res.ok) {
      const errorText = await res.text();
      console.error('❌ [getAgentPhoneCalls] Error:', res.status, errorText);
      throw new Error('No se pudo obtener la lista de llamadas del agente');
    }

    const data = await res.json();
    console.log('✅ [getAgentPhoneCalls] Response:', {
      totalElements: data.totalElements,
      totalPages: data.totalPages,
      contentLength: data.content?.length || 0
    });
    return data;
  };

  // Obtener una llamada específica por ID
  const getPhoneCallById = async (id: number): Promise<PhoneCall> => {
    const res = await apiFetch(`/phone-calls/${id}`);
    if (!res.ok) throw new Error('No se pudo obtener la llamada telefónica');
    return res.json();
  };

  // Asignar especialista a un caso
  const assignSpecialist = async (phoneCallId: number, specialistId: number, notes?: string): Promise<PhoneCallResponse> => {
    const res = await apiFetch(`/phone-calls/${phoneCallId}/assign`, {
      method: 'POST',
      body: JSON.stringify({
        specialistId,
        notes: notes || ''
      }),
    });
    if (!res.ok) throw new Error('Error al asignar especialista');
    return res.json();
  };

  // Desasignar especialista de un caso
  const unassignSpecialist = async (phoneCallId: number): Promise<PhoneCallResponse> => {
    const res = await apiFetch(`/phone-calls/${phoneCallId}/unassign`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Error al desasignar especialista');
    return res.json();
  };

  // ✅ NUEVO: Asignar o cambiar usuario de seguimiento
  const assignFollowUpUser = async (phoneCallId: number, assignedToUserId: number): Promise<PhoneCallResponse> => {
    const res = await apiFetch(`/phone-calls/${phoneCallId}/assign-follow-up`, {
      method: 'PUT',
      body: JSON.stringify({
        assignedToUserId
      }),
    });
    if (!res.ok) throw new Error('Error al asignar usuario de seguimiento');
    return res.json();
  };

  // ✅ NUEVOS ENDPOINTS DE ESPECIALISTAS SEGÚN API ACTUALIZADA

  // Obtener todos los especialistas (paginado)
  const getAllSpecialists = async (page = 0, size = 50, sortBy = 'specialistId', sortDir = 'asc', productArea?: string): Promise<SpecialistsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    if (productArea) {
      params.append('productArea', productArea);
    }
    
    console.log('🔍 Llamando a getAllSpecialists con params:', params.toString());
    
    try {
      const res = await apiFetch(`/specialists?${params.toString()}`);
      console.log('📡 Respuesta recibida:', {
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        headers: Object.fromEntries(res.headers.entries())
      });
      
      if (!res.ok) {
        console.error('❌ Error en getAllSpecialists:', res.status, res.statusText);
        const errorText = await res.text();
        console.error('❌ Error response body:', errorText);
        throw new Error('No se pudieron obtener los especialistas');
      }
      
      // Intentar obtener el texto de la respuesta primero
      const responseText = await res.text();
      console.log('📄 Response text length:', responseText.length);
      console.log('📄 Response text (first 500 chars):', responseText.substring(0, 500));
      console.log('📄 Response text (last 200 chars):', responseText.substring(Math.max(0, responseText.length - 200)));
      
      // Verificar si hay error de recursión infinita
      if (responseText.includes('Infinite recursion') || responseText.includes('StackOverflowError')) {
        console.error('❌ Error de recursión infinita detectado en el backend');
        console.error('❌ Esto indica una referencia circular en los datos del especialista');
        throw new Error('Error de recursión infinita en el backend - referencia circular en los datos');
      }
      
      // Verificar si la respuesta parece estar truncada
      if (!responseText.endsWith('}') && !responseText.endsWith(']')) {
        console.warn('⚠️ La respuesta parece estar truncada - no termina con } o ]');
        console.log('📄 Últimos 50 caracteres:', responseText.substring(Math.max(0, responseText.length - 50)));
      }
      
      // Ahora intentar parsear el JSON
      const data = JSON.parse(responseText);
      console.log('✅ getAllSpecialists respuesta exitosa:', {
        totalItems: data.totalItems,
        specialistsCount: data.specialists?.length || 0
      });
      return data;
    } catch (error) {
      console.error('❌ Error completo en getAllSpecialists:', error);
      if (error instanceof SyntaxError) {
        console.error('❌ Error de sintaxis JSON:', error.message);
        console.error('❌ Posición del error:', error.message.match(/position (\d+)/)?.[1]);
      }
      throw error;
    }
  };

  // Buscar especialistas por nombre
  const searchSpecialists = async (searchTerm: string, page = 0, size = 50): Promise<SpecialistsResponse> => {
    const params = new URLSearchParams({
      search: searchTerm,
      page: page.toString(),
      size: size.toString()
    });
    
    const res = await apiFetch(`/specialists/search?${params.toString()}`);
    if (!res.ok) throw new Error('No se pudieron buscar los especialistas');
    return res.json();
  };

  // Obtener solo especialistas activos
  const getActiveSpecialists = async (): Promise<Specialist[]> => {
    // /specialists/active no existe en backend; se usa el listado paginado filtrando activos
    const res = await getAllSpecialists(0, 200, 'fullName', 'asc');
    return (res.specialists || []).filter((s) => s.active !== false && s.isActive !== false);
  };

  // Obtener especialistas por tipo
  const getSpecialistsByType = async (type: 'PSYCHOLOGIST' | 'ADVISOR' | 'OTHER'): Promise<Specialist[]> => {
    const res = await apiFetch(`/specialists/type/${type}`);
    if (!res.ok) throw new Error(`No se pudieron obtener los especialistas de tipo ${type}`);
    return res.json();
  };

  // Obtener especialista por ID
  const getSpecialistById = async (id: number): Promise<Specialist> => {
    const res = await apiFetch(`/specialists/${id}`);
    if (!res.ok) throw new Error('No se pudo obtener el especialista');
    return res.json();
  };

  // Obtener especialista por licencia
  const getSpecialistByLicense = async (license: string): Promise<Specialist> => {
    const res = await apiFetch(`/specialists/license/${license}`);
    if (!res.ok) throw new Error('No se pudo obtener el especialista por licencia');
    return res.json();
  };

  // Obtener estadísticas de especialistas
  const getSpecialistsStats = async (): Promise<SpecialistsStats> => {
    const res = await apiFetch('/specialists/stats');
    if (!res.ok) throw new Error('No se pudieron obtener las estadísticas de especialistas');
    return res.json();
  };

  // Crear nuevo especialista
  const createSpecialist = async (specialistData: any): Promise<Specialist> => {
    console.log('🔵 [phoneCallService] createSpecialist - Datos recibidos:', specialistData);
    console.log('🔵 [phoneCallService] createSpecialist - Body JSON:', JSON.stringify(specialistData, null, 2));
    
    const res = await apiFetch('/specialists', {
      method: 'POST',
      body: JSON.stringify(specialistData),
    });
    
    console.log('🔵 [phoneCallService] createSpecialist - Response status:', res.status);
    
    if (!res.ok) throw new Error('Error al crear el especialista');
    return res.json();
  };

  // Actualizar especialista
  const updateSpecialist = async (id: number, specialistData: any): Promise<Specialist> => {
    const payload = {...specialistData, specialistId: id};
    
    console.log('🔵 [phoneCallService] updateSpecialist - ID:', id);
    console.log('🔵 [phoneCallService] updateSpecialist - Datos recibidos:', specialistData);
    console.log('🔵 [phoneCallService] updateSpecialist - Body JSON:', JSON.stringify(payload, null, 2));
    
    const res = await apiFetch(`/specialists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    
    console.log('🔵 [phoneCallService] updateSpecialist - Response status:', res.status);
    
    if (!res.ok) {
      const errorText = await res.text();
      let message = 'Error al actualizar el especialista';
      try {
        const parsed = JSON.parse(errorText);
        if (parsed?.error) message = parsed.error;
      } catch {
        if (errorText) message = errorText;
      }
      throw new Error(message);
    }
    return res.json();
  };

  /**
   * Cambiar estatus activo/inactivo con motivo (guia-front-estatus-especialista.md).
   * PATCH /api/specialists/{id}/status
   */
  const updateSpecialistStatus = async (
    id: number,
    payload: { isActive: boolean; reason: string }
  ): Promise<Specialist> => {
    const reason = (payload.reason || '').trim();
    if (!reason) throw new Error('El motivo es obligatorio');
    if (reason.length > 500) throw new Error('El motivo no puede superar 500 caracteres');

    const res = await apiFetch(`/specialists/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        isActive: payload.isActive,
        reason,
      }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      let message = 'Error al cambiar el estatus del especialista';
      try {
        const parsed = JSON.parse(errorText);
        if (parsed?.error) message = parsed.error;
      } catch {
        if (errorText) message = errorText;
      }
      throw new Error(message);
    }
    return res.json();
  };

  /**
   * Historial de cambios de estatus.
   * GET /api/specialists/{id}/status-history
   */
  const getSpecialistStatusHistory = async (
    id: number,
    page = 0,
    size = 20
  ): Promise<import('../types/phoneCapture').SpecialistStatusHistoryResponse> => {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
    });
    const res = await apiFetch(`/specialists/${id}/status-history?${params.toString()}`);
    if (!res.ok) throw new Error('No se pudo obtener el historial de estatus');
    return res.json();
  };

  // Eliminar especialista (soft delete con motivo obligatorio)
  const deleteSpecialist = async (id: number, reason: string): Promise<void> => {
    const trimmed = (reason || '').trim();
    if (!trimmed) throw new Error('El motivo es obligatorio para desactivar');
    if (trimmed.length > 500) throw new Error('El motivo no puede superar 500 caracteres');

    const res = await apiFetch(`/specialists/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({
        isActive: false,
        reason: trimmed,
      }),
    });
    if (!res.ok) {
      const errorText = await res.text();
      let message = 'Error al eliminar el especialista';
      try {
        const parsed = JSON.parse(errorText);
        if (parsed?.error) message = parsed.error;
      } catch {
        if (errorText) message = errorText;
      }
      throw new Error(message);
    }
  };

  // Eliminación permanente
  const deleteSpecialistPermanent = async (id: number): Promise<void> => {
    const res = await apiFetch(`/specialists/${id}/permanent`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Error al eliminar permanentemente el especialista');
  };

  // ✅ FUNCIÓN COMPATIBLE: Obtener especialistas disponibles (mantener para compatibilidad)
  const getAvailableSpecialists = async (tipoAtencion: string[], ciudad?: string): Promise<Specialist[]> => {
    // Usar la función de especialistas activos y filtrar por tipo de atención
    const response = await getAllSpecialists(0, 100); // Obtener más registros para filtrar
    let specialists = response.specialists.filter(s => s.isActive);
    
    // Filtrar por tipo de atención si se especifica
    if (tipoAtencion.length > 0) {
      specialists = specialists.filter(specialist => {
        return specialist.serviceAreas.some(area => 
          tipoAtencion.some(tipo => 
            area.name.toLowerCase().includes(tipo.toLowerCase()) ||
            specialist.specialties.some(specialty => 
              specialty.name.toLowerCase().includes(tipo.toLowerCase())
            )
          )
        );
      });
    }
    
    return specialists;
  };

  // Obtener estadísticas del dashboard del agente actual
  // ✅ CORREGIDO según GUIA_FRONTEND_CONSULTA_LLAMADAS.md
  const getAgentDashboardStats = async (): Promise<AgentDashboardStats> => {
    const res = await apiFetch('/phone-calls/agent/statistics');
    if (!res.ok) {
      const errorText = await res.text();
      console.error('❌ [getAgentDashboardStats] Error:', res.status, errorText);
      throw new Error('No se pudieron obtener las estadísticas del agente');
    }
    
    const backendStats = await res.json(); // Map<PhoneCallStatus, Long>
    const countOf = (key: string) => Number(backendStats?.[key]) || 0;

    const stats: AgentDashboardStats = {
      draft: countOf('DRAFT'),
      captured: countOf('CAPTURED'),
      assigned: countOf('ASSIGNED'),
      inProgress: countOf('IN_PROGRESS'),
      evidencePending: countOf('EVIDENCE_PENDING'),
      completed: countOf('COMPLETED'),
      totalCalls: Object.values(backendStats || {}).reduce(
        (sum: number, count) => sum + (Number(count) || 0),
        0
      ),
    };

    return stats;
  };

  // Cambiar estado de una llamada
  const updatePhoneCallStatus = async (id: number, status: string): Promise<PhoneCallResponse> => {
    const res = await apiFetch(`/phone-calls/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error('Error al actualizar el estado de la llamada');
    return res.json();
  };

  // Eliminar una llamada (soft delete)
  const deletePhoneCall = async (id: number): Promise<PhoneCallResponse> => {
    const res = await apiFetch(`/phone-calls/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error('Error al eliminar la llamada telefónica');
    return res.json();
  };

  // Aviso de privacidad publicado (mismo texto que /aviso-de-privacidad)
  const getPrivacyNotice = async (): Promise<{ version: string; lastUpdated: string; content: string }> => ({
    version: AVISO_PRIVACIDAD.version,
    lastUpdated: AVISO_PRIVACIDAD.lastUpdatedLabel,
    content: avisoPrivacidadPlainText()
  });

  return { 
    // Funciones de llamadas telefónicas
    createPhoneCallDraft,
    updatePhoneCallDraft,
    finalizePhoneCallDraft,
    assignSpecialistToDraftReferral,
    updatePhoneCallSeverityLevel,
    createPhoneCall,
    updatePhoneCall,
    getAgentPhoneCalls,
    getPhoneCallById,
    assignSpecialist,
    getAgentDashboardStats,
    updatePhoneCallStatus,
    deletePhoneCall,
    getPrivacyNotice,
    
    // Funciones de especialistas (nuevas)
    getAllSpecialists,
    searchSpecialists,
    getActiveSpecialists,
    getSpecialistsByType,
    getSpecialistById,
    getSpecialistByLicense,
    getSpecialistsStats,
    createSpecialist,
    updateSpecialist,
    updateSpecialistStatus,
    getSpecialistStatusHistory,
    deleteSpecialist,
    deleteSpecialistPermanent,
    unassignSpecialist,
    assignFollowUpUser,
    
    // Función compatible (mantener para compatibilidad)
    getAvailableSpecialists
  };
};